import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { getLocation } from '$utilities/location';

/* ========================================================================= */
/* Context(s) */
/* ========================================================================= */

/** @param {string} uri @returns {string} */
const noop = (uri) => uri;

const LocaleContext = createContext({
    current: null,
    options: [],
    formatURI: noop,
    translate: noop,
});

/* ========================================================================= */
/* Hook(s) */
/* ========================================================================= */


/**
 * @hook useSession
 */
const useLocale = () => {
    const context = useContext(LocaleContext);

    if (context === undefined) {
        throw new Error('useLocale must be used within a LocaleProvider.');
    }

    return context;
};

/* ========================================================================= */
/* Provider(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/contexts/locale').LocaleProviderProps}
 */
const LocaleProvider = ({
    children,
    current,
    options = [],
    isDev,
    translations
}) => {
    const [debugMode, setDebugMode] = useState(false);

    /**
     * Adds specified locale segment to a URI
     * @param {string} string
     * @returns {string}
     */
    const formatURI = (uri, locale) => {
        try {
            const location = getLocation();

            const url = new URL(uri, location.origin);

            // Ignore external links
            if (url.origin !== location.origin) { return url.href; }

            let segments = url.pathname.split('/').filter(s => s.length > 0);

            // Remove any existing language segment if found
            if (segments.length > 0 && options.find((o) => o.segment === segments[0])) {
                segments.shift();
            }

            // Select required language segment
            const localeSegment = locale !== undefined ? options.find((o) => o.code === locale)?.segment || null : current?.segment;

            // Compose new path
            url.pathname = `${localeSegment ? `/${localeSegment}` : ''}/${segments.join('/')}`;

            return url.href.replace(/\/$/, '');
        } catch (err) {
            console.error(err);
            return uri;
        }
    };

    /**
     * Translate a string
     * @param {string} key
     * @param {Record<string, string>} [variables]
     * @returns {string}
     */
    const translate = (key, variables) => {
        let translatedTerm = key;

        if (translations && !debugMode) {
            translations.some((entry) => {
                if (entry.key === key) {
                    const translationKey = `translation__${current?.translationKey || 'en_US'}`;
                    if (translationKey in entry) {
                        // @ts-expect-error
                        translatedTerm = entry[translationKey];
                    }

                    if (variables) {
                        translatedTerm = translatedTerm.replace(/{(.*?)}/g, (match, offset) => variables[offset]);
                    }
                }
            });
        }

        return translatedTerm;
    };


    /* NOTE: Toggle debug mode with CTRL+SHIFT+D */
    useEffect(() => {
        const gridKeyHandler = (e) => {
            if (e.ctrlKey && e.shiftKey && String(e.key).toUpperCase() === 'D') {
                setDebugMode((v) => !v);
            }
        };

        document.addEventListener('keydown', gridKeyHandler);
        return () => document.removeEventListener('keydown', gridKeyHandler);
    }, []);


    return (
        <LocaleContext.Provider value={useMemo(() => {
            return {
                options,
                current,
                formatURI,
                translate
            };
        }, [
            options,
            current,
            formatURI,
            translate
        ])}>
            {children}
        </LocaleContext.Provider>
    );
};

/* ========================================================================= */
/* Export(s) */
/* ========================================================================= */

export {
    LocaleProvider,
    useLocale
};
